import { useEffect, useId, useRef, useState } from 'react'
import { IconFolderTile, PlusOutlined } from '../../../components/icons'
import { Button } from '../../../components/ui/Button'
import { DropdownMenu } from '../../../components/ui/DropdownMenu'
import type { WorkspaceSummary } from '../../workspaces/types'
import { type AddToProjectService, defaultAddToProjectService } from '../add-to-project-service'

export interface AddToProjectMenuProps {
  /** The library part id(s) being linked — the whole current bulk selection. */
  readonly partIds: readonly string[]
  /** Existing projects the caller may link into — fetched by the caller
   * (mirrors the old modal's injectable-service seam) rather than this control
   * owning its own `GET /workspaces` fetch. */
  readonly workspaces: readonly WorkspaceSummary[]
  /** Called once the batch is successfully linked, with the target project's
   * name and how many parts were linked — the caller toasts, clears the
   * selection, and refetches. */
  readonly onLinked: (workspaceName: string, count: number) => void
  /** Injectable for tests / future service swap. */
  readonly service?: AddToProjectService
}

/**
 * UID-418 — "Add to project" as the design's anchored OVERLAY (`Fictiv
 * Platform.dc.html:1322-1340`, `libBomMenu`), not a centered modal: a
 * button-anchored popover that opens above the fixed-bottom bulk bar with two
 * sections — a list of existing projects (each an open-folder tile +
 * name) on top, then a divider, then a "New project" CTA with a plus icon.
 * Built on the shared `DropdownMenu` shell (relative-wrapper + absolute
 * `role="menu"` panel + outside-click/Escape/roving-focus), the same primitive
 * every other staging dropdown uses.
 */
export function AddToProjectMenu({
  partIds,
  workspaces,
  onLinked,
  service = defaultAddToProjectService,
}: AddToProjectMenuProps) {
  return (
    <DropdownMenu
      className="add-to-project-menu"
      menuLabel="Add to project"
      renderTrigger={({ open, toggle, triggerRef }) => (
        <Button
          ref={triggerRef}
          variant="outline"
          size="sm"
          className="bulk-action-bar-btn"
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={toggle}
        >
          Add to project
        </Button>
      )}
      // renderMenu is only rendered while the menu is open, so `AddToProjectPanel`
      // mounts fresh on each open — its "New project" inline-create state resets
      // for free without this component tracking the menu's open flag.
      renderMenu={() => (
        <AddToProjectPanel
          partIds={partIds}
          workspaces={workspaces}
          service={service}
          onLinked={onLinked}
        />
      )}
    />
  )
}

/** Number of folder-tile tints (must match the `.add-to-project-tile--N`
 * classes in parts.css and the design's 4-way palette at `:6863`). */
const PROJECT_TILE_TINTS = 4

/**
 * UID-418 — the design gives each project a colored folder tile from a fixed
 * palette, assigned by the project's own position/identity (`:6863`). We derive
 * a STABLE index from the workspace id (a small deterministic char-sum hash) so
 * a given project always shows the same tint across renders and sessions,
 * without needing a color field on `WorkspaceSummary`.
 */
function projectTileTint(workspaceId: string): number {
  let sum = 0
  for (let i = 0; i < workspaceId.length; i += 1) {
    sum = (sum + workspaceId.charCodeAt(i)) % PROJECT_TILE_TINTS
  }
  return sum
}

interface AddToProjectPanelProps {
  readonly partIds: readonly string[]
  readonly workspaces: readonly WorkspaceSummary[]
  readonly service: AddToProjectService
  readonly onLinked: (workspaceName: string, count: number) => void
}

function AddToProjectPanel({ partIds, workspaces, service, onLinked }: AddToProjectPanelProps) {
  const [creating, setCreating] = useState(false)
  const [newName, setNewName] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const nameId = useId()
  const nameInputRef = useRef<HTMLInputElement>(null)

  // Focus the name field the moment the "New project" branch opens (the
  // `autoFocus` attribute is disallowed by our a11y lint rule, so this does the
  // same thing imperatively once the input has mounted).
  useEffect(() => {
    if (creating) nameInputRef.current?.focus()
  }, [creating])

  async function linkExisting(workspace: WorkspaceSummary) {
    setBusy(true)
    setError(null)
    for (const partId of partIds) {
      const outcome = await service.addPartToWorkspace(partId, { workspaceId: workspace.id })
      if (!outcome.ok) {
        setError(outcome.message)
        setBusy(false)
        return
      }
    }
    // Success: hand off to the caller (which clears the selection, unmounting
    // this panel) — so no further local state is touched here.
    onLinked(workspace.name, partIds.length)
  }

  async function createAndLink() {
    const name = newName.trim()
    if (!name) return
    setBusy(true)
    setError(null)
    // The first call creates the workspace; every subsequent part reuses the
    // resulting id so a "New project" of N parts creates exactly ONE project
    // (never one throwaway workspace per part).
    let workspaceId: string | undefined
    for (const partId of partIds) {
      const outcome = await service.addPartToWorkspace(
        partId,
        workspaceId ? { workspaceId } : { newWorkspace: { name, visibility: 'private' } },
      )
      if (!outcome.ok) {
        setError(outcome.message)
        setBusy(false)
        return
      }
      workspaceId = outcome.link.workspace.id
    }
    onLinked(name, partIds.length)
  }

  if (creating) {
    return (
      <div className="add-to-project-create">
        {/* Errors are surfaced as TEXT (never color-only, WCAG 3.3.1). */}
        {error && (
          <p role="alert" className="add-to-project-error">
            {error}
          </p>
        )}
        <label htmlFor={nameId} className="add-to-project-create-label">
          Project name
        </label>
        <input
          ref={nameInputRef}
          id={nameId}
          type="text"
          className="add-to-project-create-input"
          value={newName}
          onChange={(event) => setNewName(event.target.value)}
          placeholder="e.g. Q3 tooling refresh"
        />
        <div className="add-to-project-create-actions">
          <Button
            variant="link"
            size="sm"
            onClick={() => {
              setCreating(false)
              setError(null)
            }}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => void createAndLink()}
            loading={busy}
            disabled={newName.trim().length === 0}
          >
            Create
          </Button>
        </div>
      </div>
    )
  }

  return (
    <>
      {error && (
        <p role="alert" className="add-to-project-error">
          {error}
        </p>
      )}
      <div className="dropdown-menu-header">Add to project</div>
      {workspaces.length === 0 && <p className="dropdown-menu-empty">No projects yet.</p>}
      {workspaces.map((workspace) => (
        <button
          key={workspace.id}
          type="button"
          role="menuitem"
          className="dropdown-menu-item"
          onClick={() => void linkExisting(workspace)}
          disabled={busy}
        >
          <span
            className={`add-to-project-tile add-to-project-tile--${projectTileTint(workspace.id)}`}
            aria-hidden="true"
          >
            <IconFolderTile size={15} />
          </span>
          <span className="add-to-project-name">{workspace.name}</span>
        </button>
      ))}
      <div className="dropdown-menu-divider" aria-hidden="true" />
      <button
        type="button"
        role="menuitem"
        className="dropdown-menu-item dropdown-menu-item-info"
        onClick={() => setCreating(true)}
        disabled={busy}
      >
        <span className="add-to-project-tile add-to-project-tile-new" aria-hidden="true">
          <PlusOutlined />
        </span>
        New project
      </button>
    </>
  )
}
